import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * The parts a text control needs to wear the product's field format.
 *
 * The format is one control with five states, and the label lives *inside* it: at rest the
 * label is the control's own placeholder, and the moment the field is focused or holds a
 * value the label lifts into a notch in the top border. Fields used to carry a separate
 * `<Label>` stacked above them, which is a second block of type per field and the reason a
 * form of eight questions was two screens tall.
 *
 * The resting label is the *native* placeholder rather than a positioned element, because a
 * positioned one has to guess the control's padding - and guesses wrong the moment a field
 * carries a search icon, a prefix or a right-to-left locale.
 *
 *   default   1px hairline, page fill, label sitting inside
 *   hover     the fill steps to `muted`, the line is unchanged
 *   focus     the line thickens to 2px in the brand, label notched and brand with it
 *   error     the same 2px, in the destructive colour, label and message with it
 *   disabled  the line and the label both drop to muted, the control does not respond
 *
 * `Input` and `Textarea` both render through these, so there is one implementation of the
 * notch rather than one per control.
 */

/**
 * A label as the placeholder attribute has to have it: plain text.
 *
 * A labelled field shows its label *inside* the box at rest - that is how anyone knows what
 * goes there before touching it - and lifts the same words into the notch on focus. A label
 * carrying a required asterisk is a node, and `placeholder` is a string, so it is flattened
 * here.
 */
export function nodeText(node: React.ReactNode): string {
	if (node === null || node === undefined || typeof node === 'boolean') return '';
	if (typeof node === 'string' || typeof node === 'number') return String(node);
	if (Array.isArray(node)) return node.map(nodeText).join('');
	if (React.isValidElement(node)) return nodeText((node.props as { children?: React.ReactNode }).children);
	return '';
}

/** These render their own content, so the label can never sit on top of them. */
export const ALWAYS_LIFTED = new Set(['date', 'datetime-local', 'month', 'time', 'week', 'color', 'range']);

/**
 * The shell for a control that carries its label in the border notch but is **not** an
 * `<input>` - a Select trigger, a Popover combobox, a multi-select. `Input` does all of
 * this itself; everything else in this product is a button with a menu behind it, and this
 * is that button's shell.
 *
 * `lifted` is the caller's own answer to "does this hold a value". A menu-backed control
 * has no `filled` state to read, and its menu covers the box while it is open, so the value
 * is the only thing worth reacting to - unlike a text field, where focus alone has to lift
 * the label out of the way of what is being typed.
 *
 * At rest the trigger shows the label as its own placeholder, exactly as a text field does;
 * the moment it holds a value the label moves into the notch and the value takes the box.
 */
export function NotchedField({
	id,
	label,
	lifted,
	invalid,
	disabled,
	className,
	children,
}: FieldStateProps & {
	id?: string;
	label: React.ReactNode;
	className?: string;
	children: React.ReactNode;
}) {
	return (
		<FieldShell className={className}>
			{children}
			{lifted ? (
				<FieldLabel htmlFor={id ?? ''} invalid={invalid} disabled={disabled}>
					{label}
				</FieldLabel>
			) : null}
		</FieldShell>
	);
}

export interface FieldStateProps {
	lifted: boolean;
	invalid?: boolean;
	disabled?: boolean;
}

/**
 * The control's shell: relative, because the notched label is positioned against it.
 */
export function FieldShell({
	className,
	children,
}: {
	className?: string;
	children: React.ReactNode;
}) {
	return <div className={cn('relative w-full', className)}>{children}</div>;
}

/**
 * The lifted label: a chip of the control's own fill sitting on the border line. The line is
 * not really cut, it is covered, which is the whole trick and the reason the control's fill
 * and the label's fill are the same token. It renders only while lifted; at rest the
 * placeholder is the label.
 *
 * ponytail: the notch paints `bg-surface` to hide the border behind it, so a field dropped
 * on a surface of a different colour shows a seam. Swap to a `fieldset`/`legend` outline if
 * that ever happens for real.
 */
/**
 * The notch chip's own look, shared by the `<label>` a text field renders beside its input
 * and the `<span>` a Select trigger renders *inside* itself. A trigger cannot use the
 * `<label>` form: it is wrapped in a form-library `Slot` that clones its single child, so
 * putting a positioning wrapper around it would take the ref and the aria wiring with it.
 */
export const FIELD_NOTCH = [
	// `field-notch` is a hook, not a style: the chip paints the control's resting fill to
	// hide the border behind it, so a panel in any other colour repaints it by that name.
	//
	// `z-20` because the chip must outrank the control it names. A compound field raises its
	// own halves to keep one half's border from being cut by the neighbour's - the phone
	// field's country trigger is `hover:z-10 focus:z-10`, its number half `focus-visible:z-10`
	// - and a positioned sibling with a z-index beats this chip's auto one. So hovering the
	// flag painted the trigger's own fill over the label and "Contact number" lost its first
	// word. Nothing is clickable here (`pointer-events-none`), so being on top costs nothing.
	'field-notch pointer-events-none absolute left-2.5 top-0 z-20 max-w-[calc(100%-1.5rem)] -translate-y-1/2 select-none truncate',
	'bg-surface px-1 text-2xs font-semibold leading-none text-foreground',
].join(' ');

export function FieldLabel({
	htmlFor,
	children,
	focused,
	invalid,
	disabled,
}: Omit<FieldStateProps, 'lifted'> & {
	htmlFor: string;
	children: React.ReactNode;
	/** A focused control is brand from its border to its label - one colour, not two. */
	focused?: boolean;
}) {
	return (
		<label
			htmlFor={htmlFor}
			// So a trigger can be named by its label *and* its value (aria-labelledby).
			id={htmlFor ? `${htmlFor}-label` : undefined}
			className={cn(
				FIELD_NOTCH,
				focused && 'text-field-focus',
				invalid && 'text-destructive',
				disabled && 'text-muted-foreground',
			)}>
			{children}
		</label>
	);
}

/**
 * The control itself, as classes. Every field-shaped thing in the product renders these -
 * `Input`, `Textarea`, `SelectTrigger`, the command palette's search row, a date picker's
 * trigger, a custom combobox. A screen may say where a field sits and how wide it is; what
 * it looks like is decided here and nowhere else.
 *
 * The resting state is deliberately quiet: a hairline, the page's own white, muted
 * placeholder text. Purple is spent on focus and on nothing else - a field at rest that
 * carries the brand colour competes with the content it is there to collect.
 */
export const FIELD_BASE = [
	'w-full rounded-sm border bg-surface text-sm text-foreground',
	'transition-[background-color,border-color,box-shadow] duration-fast ease-out',
	'placeholder:text-placeholder placeholder:font-normal',
	'focus-visible:outline-none focus:outline-none',
	// A field-shaped trigger is often rendered *on* `Button` (the date range picker, the
	// faceted filter, the multi-selects), so it arrives carrying the button's offset focus
	// and its offset focus ring. Both are button affordances: clicking a filter then felt
	// like pressing a button instead of opening a field. Cancel them here - this string is
	// passed as the button's `className`, so tailwind-merge drops the button's own copies.
	'focus-visible:ring-0 focus-visible:ring-offset-0',
].join(' ');

/** One height for every single-line control: input, select trigger, date trigger. */
export const FIELD_SIZE = 'h-field px-3 py-2';

/**
 * The Playground toolbar's own compact scale - one step down from a full-height form
 * field, for a row that fits six controls (search, two filters, two dropdowns, a primary
 * button) rather than the single field `FIELD_SIZE` was sized for. 2rem (`h-8`), the
 * toolbar's own request - not `FIELD_SIZE`'s 2.5rem, and not the custom `h-field` key
 * either: `h-8` is a standard Tailwind class, so `tailwind-merge` correctly drops a
 * component's own competing `h-10`/`h-9` instead of leaving both in the class list.
 * `py-1` rather than `FIELD_SIZE`'s `py-2` because 2rem minus a 2px border leaves less
 * room to pad before `text-xs` stops fitting on one line.
 *
 * Every consumer still renders as a plain element rather than through a component that
 * injects its own height class (`Button`'s default size, say) - belt and braces, since a
 * *standard* height here removes the specific bug that bit `h-field`, but stacking two
 * conflicting height utilities is still two sources of truth for one box.
 */
export const PLAYGROUND_CONTROL = 'h-8 px-3 py-1 text-xs';

export function fieldClasses({ lifted, invalid, disabled }: FieldStateProps) {
	return cn(
		FIELD_BASE,
		// The line is already at 3:1; a second, darker line on hover reads as a state the
		// control is not in yet, so hover moves the fill instead.
		!disabled && !invalid && 'border-border-control',
		// Hover fills rather than darkens the line - but only at rest: once the label has
		// lifted, its chip is painted in the control's resting fill and a hover tint behind
		// it would show the seam.
		!disabled && !invalid && !lifted && 'hover:bg-muted/60',
		// Focus and error are the same 2px line in two colours, drawn inside the control so
		// nothing on the row moves when it lands.
		!invalid && 'focus-visible:border-field-focus focus-visible:bg-surface focus-visible:shadow-field-focus',
		// An open trigger is a focused field: the menu is the field's own surface. Same rule
		// as `.field-trigger[data-state='open']` in index.css; a plain input never has the
		// attribute, so it costs those nothing.
		!invalid && 'data-[state=open]:border-field-focus data-[state=open]:bg-surface data-[state=open]:shadow-field-focus',
		invalid && 'border-destructive shadow-field-error',
		'disabled:cursor-not-allowed disabled:border-border disabled:bg-surface-sunken disabled:text-muted-foreground disabled:hover:bg-surface-sunken',
	);
}
